import {CopilotIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'
import type React from 'react'

import styles from './CopilotAvatar.module.css'

type Size = 'small' | 'medium'

const iconSizeMap = {
  small: {default: 12, minimal: 16},
  medium: {default: 16, minimal: 20},
}

export interface CopilotAvatarProps extends Omit<React.ComponentPropsWithoutRef<typeof CopilotIcon>, 'size'> {
  size?: Size
  minimal?: boolean
  className?: string
  isDecorative?: boolean
}

/**
 * Lightweight Copilot avatar that renders a static icon.
 * For the animated large variant, use `CopilotAvatarAnimated` instead.
 */
export function CopilotAvatar({
  size = 'small',
  minimal = false,
  className = '',
  isDecorative = false,
}: CopilotAvatarProps): React.ReactElement {
  const actualIconSize = minimal ? iconSizeMap[size].minimal : iconSizeMap[size].default

  return (
    <span
      className={clsx(styles.copilotAvatar, styles[size], !minimal && styles.defaultStyle, className)}
      data-testid="copilot-avatar"
      role={isDecorative ? 'presentation' : 'img'}
      aria-label={isDecorative ? undefined : 'Copilot avatar'}
    >
      <CopilotIcon size={actualIconSize} />
    </span>
  )
}
